import errorFromHttpResponse from '../utils/error-from-http-response'
import NetworkService from './NetworkService'

// TASK-SF-06 -- client-side contracts for the Student Foundation reference
// taxonomy (TASK-SF-03) and the authenticated student profile surface
// (TASK-SF-05).
//
// Shape mirrors AuthService: unwrap `res.data`, and route every HTTP failure
// through errorFromHttpResponse so callers get one consistent error object
// (`err.message` from the server's `{ err }` body, `err.status` from the
// response). This module is deliberately free of Vuex, i18n and component
// concerns -- the onboarding wizard (SF-07) and the profile screens
// (SF-08/SF-09) consume it.

export default {
  // GET /api/reference/education
  //   -> { stages: [ { name, displayName, displayOrder,
  //                    grades: [...], studyFields: [...] } ],
  //        provinces: [ { name, displayName, displayOrder } ] }
  // One request serves both the مقطع->پایه and the مقطع->رشته dependency:
  // grades and studyFields are already nested under each stage, so the
  // wizard never issues a second request for either.
  getEducationReference() {
    return NetworkService.getEducationReference()
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // GET /api/reference/education/cities?province=<slug> -> { cities: [...] }
  // Fetched lazily, only once a province is chosen on the profile edit
  // screen (DESIGN-11); city is not collected during onboarding. An empty
  // slug short-circuits to an empty list rather than issuing a 422 request.
  getCitiesByProvince(provinceSlug) {
    if (!provinceSlug) return Promise.resolve({ cities: [] })
    return NetworkService.getEducationReferenceCities(provinceSlug)
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // GET /api/reference/education/courses?stage=<>&grade=<>&studyField=<>
  //   -> { courses: [ { name, displayName, displayOrder } ] }
  // P4 AI Tutor WP-02 -- the AI Tutor setup screen's "subject" (درس) picker.
  // This is a DIFFERENT reference concept from studyField (رشته, an
  // education track/major): a course is a specific curriculum subject
  // (e.g. حسابان ۱), scoped to the مقطع/پایه/رشته already chosen. `grade`
  // and `studyField` are optional narrowing filters -- omit either until
  // the student has made that selection.
  getCourses({ educationStage, educationGrade, studyField } = {}) {
    if (!educationStage) return Promise.resolve({ courses: [] })
    return NetworkService.getEducationReferenceCourses(
      educationStage,
      educationGrade,
      studyField
    )
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // GET /api/student/profile -> the tagged-union profile object (§5.3):
  //   { firstName, lastName, phone, email|null,
  //     educationStage|null, educationGrade|null, studyField|null,
  //     province|null,
  //     city: { type:'lookup', name, displayName } | { type:'manual', value } | null,
  //     schoolName|null }
  getProfile() {
    return NetworkService.getStudentProfile()
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // PATCH /api/student/profile -- send ONLY the keys the user changed. A key
  // present as null clears that field; `citySlug` and `cityManual` are
  // mutually exclusive and the caller must never pass both (the server
  // returns 422 if it does). Response has the same shape as getProfile().
  updateProfile(patch) {
    return NetworkService.patchStudentProfile(patch || {})
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // POST /api/user/phone (P43-04) -- body { ownershipGrantId } only: the number
  // comes from the verified grant on the server, never from here. Resolves with
  // the profile (same shape as getProfile()). A failure carries `status` and,
  // when the server names one, the server's own `code`: PHONE_GRANT_INVALID
  // (422), PHONE_IN_USE or PHONE_ALREADY_SET (409). It is read from the
  // response body: the Axios error's own `code` ('ERR_BAD_REQUEST') is not it.
  attachPhone(ownershipGrantId) {
    return NetworkService.attachStudentPhone({ ownershipGrantId })
      .then(res => ({ ...res.data }))
      .catch(err => {
        // a real Axios error nests the response; the response itself is also
        // accepted (the shape the other specs reject with)
        const response = (err && err.response) || err || {}
        const data = response.data || {}
        const error = new Error(
          data.err || (err && err.message) || 'Request failed'
        )
        error.status = response.status || (err && err.status)
        if (data.code) error.code = data.code
        throw error
      })
  },
}
